import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import { keys } from "../dossier";
import { Plus } from "lucide-react";
import { ErrorState, PageHeader, Skeleton, StatusBadge } from "../components/ui";
import { EditDossierButton } from "../components/dossier-edit";
import { RolloverButton } from "../components/rollover";
import { useDossier } from "../dossier";
import { label, w, locale } from "../i18n";
import { deriveIssues } from "../issues";
import { readinessLabel, readinessStatus } from "../status";

const SCOPE = ["A", "B", "C", "D", "E"];
const MAX_ATTENTION = 6;

function Step({ label, value, total, sub, connected = true }: {
  label: string; value?: number; total?: number; sub: string; connected?: boolean;
}) {
  const share = connected && total ? Math.round(((value || 0) / total) * 100) : 0;
  return <div className="ws-step">
    <div className="ws-step-label">{label}</div>
    {connected
      ? <div className="ws-step-value">{value ?? w("common.notAvailable")}<span className="ws-faint" style={{ fontSize: 14 }}>{total != null ? ` / ${total}` : ""}</span></div>
      : <div className="ws-step-value ws-faint">{w("common.notAvailable")}</div>}
    <div className="ws-step-sub">{connected ? sub : w("overview.notConnected")}</div>
    {connected && <div className="ws-bar" aria-hidden="true"><span style={{ width: `${share}%` }} /></div>}
  </div>;
}

/** The one thing to do next, in workflow order: import, check, draft, review, export. */
function NextStep({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return <section className="ws-card ws-card-pad ws-next" aria-label={w("next.eyebrow")}>
    <div><div className="ws-next-eyebrow">{w("next.eyebrow")}</div><h2 className="ws-next-title">{title}</h2>
      {body && <p className="ws-muted" style={{ margin: 0 }}>{body}</p>}</div>
    {action}
  </section>;
}

export function OverviewPage() {
  const { dossier, documents, readiness, openUpload, inspect } = useDossier();
  const base = `/dossiers/${dossier.id}`;
  const counts = readiness.data?.documents;
  const processed = counts ? counts.ready + counts.needs_review + counts.failed : undefined;
  const lang = locale();
  const audits = useResource(keys.reportAudits(dossier.id, lang), () => api.reportAudits(dossier.id, lang));
  const issues = deriveIssues(documents.data, readiness.data, audits.data);
  const chapters = (readiness.data?.chapters || []).filter(chapter => SCOPE.includes(chapter.code));
  const summary = useResource(keys.generationSummary(dossier.id), () => api.generationSummary(dossier.id));
  const reviews = useResource(keys.reviews(dossier.id), () => api.reviewSummary(dossier.id));
  const drafted = summary.data?.items.filter(g => g.status === "succeeded" && (g.block_status === "produced" || g.block_status === "audit_ko")).length;
  const draftedCodes = new Set(summary.data?.items.filter(g => g.status === "succeeded"
    && (g.block_status === "produced" || g.block_status === "audit_ko")).map(g => g.section_code));
  const toCheck = counts ? counts.needs_review + counts.failed : 0;
  const draftable = chapters.map(chapter => ({ chapter, sections: chapter.sections.filter(section =>
    section.status !== "NOT_READY" && !draftedCodes.has(section.code)) })).find(item => item.sections.length > 0);
  const missing = chapters.flatMap(chapter => chapter.sections).filter(section => section.status === "NOT_READY").length;
  const approved = reviews.data?.counts.approved ?? 0;
  const reviewable = reviews.data?.reviewable ?? 0;

  const next = (() => {
    if (!counts || !summary.data) return null;
    if (counts.total === 0) return <NextStep title={w("next.import")} body={w("next.importBody")}
      action={<button className="ws-btn primary" onClick={() => openUpload()}>{w("docs.add")}</button>} />;
    if (counts.processing > 0) return <NextStep title={w("next.processing", { count: counts.processing })} body={w("next.processingBody")} />;
    if (toCheck > 0) return <NextStep title={w("next.check", { count: toCheck })} body={w("next.checkBody")}
      action={<Link className="ws-btn primary" to={`${base}/documents`}>{w("next.checkAction")}</Link>} />;
    if (draftable) return <NextStep title={w("next.draft", { code: draftable.chapter.code, title: draftable.chapter.title })}
      body={w("next.draftBody", { count: draftable.sections.length })}
      action={<Link className="ws-btn primary" to={`${base}/chapters/${draftable.chapter.code}`}>{w("next.draftAction")}</Link>} />;
    if (approved < reviewable) return <NextStep title={w("next.review")} body={w("next.reviewBody", { approved, total: reviewable })}
      action={<Link className="ws-btn primary" to={`${base}/report`}>{w("next.reviewAction")}</Link>} />;
    if (missing > 0) return <NextStep title={w("next.missing", { count: missing })} body={w("next.missingBody")}
      action={<Link className="ws-btn primary" to={`${base}/readiness`}>{w("next.missingAction")}</Link>} />;
    return <NextStep title={w("next.export")} body={w("next.exportBody")}
      action={<a className="ws-btn primary" href={api.reportExportUrl(dossier.id, lang)} download>{w("next.exportAction")}</a>} />;
  })();

  return <>
    <PageHeader eyebrow={`${dossier.report_type} ${dossier.reporting_year}`} title={dossier.entity_name}
      lead={w("overview.period", { year: dossier.reporting_year })}
      actions={<div className="ws-row"><EditDossierButton /><RolloverButton />
        <button className="ws-btn" onClick={() => openUpload()}><Plus size={16} />{w("docs.add")}</button></div>} />

    {readiness.error && <ErrorState title={w("ready.loadError")} error={readiness.error} onRetry={readiness.reload} />}
    {next}

    <section className="ws-card ws-steps" aria-label="Workflow">
      <Step label={w("overview.inputs")} value={counts?.ready} total={counts?.total} sub={w("overview.inputsSub")} />
      <Step label={w("overview.processing")} value={processed} total={counts?.total} sub={w("overview.processingSub")} />
      <Step label={w("overview.generation")} value={drafted} total={summary.data?.supported.length} sub={w("overview.generationSub")}
        connected={!!summary.data} />
      <Step label={w("overview.review")} value={reviews.data?.counts.approved ?? 0} total={reviews.data?.reviewable}
        sub={w("overview.reviewSub")} connected={!!reviews.data} />
    </section>

    <div className="ws-grid-2" style={{ marginTop: 16 }}>
      <section className="ws-card">
        <div className="ws-card-head"><h2>{w("overview.readiness")}</h2>
          <Link className="ws-link ws-small" to={`${base}/readiness`}>{w("overview.openReadiness")}</Link></div>
        <ul className="ws-list">
          {!readiness.data && Array.from({ length: 5 }, (_, i) => <li key={i}><Skeleton width="45%" /><Skeleton width={70} /></li>)}
          {readiness.data && Object.entries(readiness.data.available_sources).map(([key, source]) => <li key={key}>
            <span>{label("source", key, source.label)}</span>
            <StatusBadge status={source.available ? "ready" : source.pending ? "needs_review" : "missing"} />
          </li>)}
        </ul>
      </section>

      <section className="ws-card">
        <div className="ws-card-head"><h2>{w("overview.attention")}</h2>
          {issues.length > 0 && <span className="ws-small ws-muted">{w("overview.attentionCount", { count: issues.length })}</span>}</div>
        {readiness.data && !issues.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w("overview.allClear")}</p>}
        <ul className="ws-list ws-attention">{issues.slice(0, MAX_ATTENTION).map(issue => <li key={issue.id}>
          <span className="ws-attention-scope">{issue.scope}</span>
          <span className="ws-list-title" style={{ flex: 1 }}>{issue.message}</span>
          {issue.documentId
            ? <button className="ws-btn sm" onClick={() => inspect(issue.documentId!)}>{w("common.open")}</button>
            : issue.sectionCode
              ? <Link className="ws-btn sm" to={`${base}/sections/${issue.sectionCode}`}>{w("common.open")}</Link> : null}
        </li>)}</ul>
        {issues.length > MAX_ATTENTION && <div className="ws-card-pad" style={{ paddingTop: 8 }}>
          <Link className="ws-link ws-small" to={`${base}/issues`}>{w("nav.issues")} ({issues.length})</Link></div>}
      </section>
    </div>

    <section className="ws-card" style={{ marginTop: 16 }}>
      <div className="ws-card-head"><h2>{w("overview.chapters")}</h2></div>
      {!readiness.data && <div className="ws-card-pad"><Skeleton width="60%" /></div>}
      {chapters.map(chapter => <div key={chapter.code}>
        <div className="ws-card-pad" style={{ paddingBottom: 4 }}><strong>{w("nav.chapterTitle", { code: chapter.code, title: chapter.title })}</strong></div>
        <ul className="ws-list">{chapter.sections.map(section => <li key={section.code}>
          <Link className="ws-link" to={`${base}/sections/${section.code}`}><span className="ws-mono">{section.code}</span>&nbsp; {section.title}</Link>
          <StatusBadge status={readinessStatus(section.status)} label={readinessLabel(section.status)} />
        </li>)}</ul>
      </div>)}
    </section>
  </>;
}
